import {
    BaseQueryFn,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { camelizeKeys, decamelizeKeys } from "humps";
import { camelCase } from "lodash";
import { getCookie } from "../utils/cookieUtils";

export interface Pagination {
    first?: number;
    last?: number;
    next?: number;
    previous?: number;
}

export interface PaginationData {
    meta?: {
        pagination?: {
            first?: string;
            last?: string;
            next?: string;
            previous?: string;
        };
    };
}

function parsePagination(data: PaginationData): Pagination {
    const pagination = data?.meta?.pagination;

    if (!pagination) return {};

    return Object.keys(pagination).reduce((acc, key) => {
        const pageNumber = new URL(
            pagination[key as keyof typeof pagination] as string,
            "http://example.com",
        ).searchParams.get("page");

        // eslint-disable-next-line no-param-reassign
        acc[key as keyof Pagination] = parseInt(pageNumber as string, 10);

        return acc;
    }, {} as Pagination);
}

const fetchArnoldBaseQuery = fetchBaseQuery({
    baseUrl: "/",
    credentials: "include",
    prepareHeaders: (headers) => {
        headers.set("Accept", "application/json");
        headers.set("Content-Type", "application/json");

        const csrfToken =
            getCookie("XSRF-TOKEN") ||
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

        if (csrfToken) {
            headers.set("X-CSRF-Token", csrfToken);
        }

        return headers;
    },
});

export interface ErrorSource {
    pointer?: string | null;
}

export interface Error {
    status: number;
    code: string;
    source?: ErrorSource | null;
    detail: string;
}

export type ArnoldBaseQueryError = {
    status: number | string;
    error: string;
    data?: {
        errors?: Error[];
    };
};

const serializeError = (error: FetchBaseQueryError): ArnoldBaseQueryError => {
    switch (error.status) {
        case "FETCH_ERROR":
        case "PARSING_ERROR":
        case "CUSTOM_ERROR": {
            return {
                status: error.status,
                error: error.error,
            };
        }
        default: {
            const errors = (error.data as { errors?: Error[] | null } | null)?.errors ?? [];

            const parsedErrors: Error[] = errors.map((error: Error) => {
                const pointer = error.source?.pointer;

                if (pointer) {
                    const path = pointer.split(".");

                    path[path.length - 1] = camelCase(path[path.length - 1]);

                    return {
                        ...error,
                        source: {
                            pointer: path.join("."),
                        },
                    };
                }

                return error;
            });

            return {
                status: error.status,
                error: "Arnold API Error",
                data: {
                    errors: parsedErrors,
                },
            };
        }
    }
};

const serializeData = (data: unknown) => {
    if (data === null || data === undefined) {
        return data;
    }

    const camelizedData = camelizeKeys(data);
    if (Array.isArray(camelizedData)) {
        return camelizedData;
    }

    return {
        ...camelizedData,
        meta: {
            ...parsePagination(camelizedData as PaginationData),
        },
    };
};

export const arnoldBaseQuery: BaseQueryFn<FetchArgs, unknown, ArnoldBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    const preparedArgs: FetchArgs = {
        ...args,
        body: args.body ? decamelizeKeys(args.body) : undefined,
        params: args.params ? decamelizeKeys(args.params) : undefined,
    };
    const { data, error, meta } = await fetchArnoldBaseQuery(preparedArgs, api, extraOptions);

    if (error) {
        return { error: serializeError(error), meta };
    }

    return {
        data: data !== undefined ? serializeData(data) : null,
        meta,
    };
};
